import React, { useState } from 'react';
import { Box, Typography, Divider, Button, makeStyles, IconButton, Paper } from '@material-ui/core';
import SubText from '../Student/EngagementCards/StudentAccessmentAcceleratorTableCard/components/SubText';
import AttemptsDetails from '../Student/EngagementCards/StudentAccessmentAcceleratorTableCard/components/AttemptsDetails';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import useStore from 'store';
import {
  KeyStudentEngagementAssessmentAcceleratorModuleResult,
  Student
} from 'types/schema-generated-types';
import formatUiDate from 'utilities/format-ui-date';
import GradeLevelChip from './GradeLevelChip';
import { useTranslation } from 'react-i18next';
import { ArrowForward } from '@material-ui/icons';
import PrintProvider, { usePrint } from 'components/ReportsModule/providers/PrintProvider';
import makePrintStyles from '../hooks/make-print-styles';

type Props = {
  module: string;
  moduleData: KeyStudentEngagementAssessmentAcceleratorModuleResult;
  studentData?: Student;
};

const ViewAllAttemptsModal = ({ module, moduleData, studentData }: Props) => {
  const [, { closeModal }] = useStore();
  const { t } = useTranslation('GradeLevel');
  const classes = useStyles();
  const printClasses = usePrintStyles();
  const [printRef, { handlePrint }] = usePrint();
  const [state, setState] = useState<{ attempt: any; completedOn: ''; attemptNumber: null }>({
    attempt: null,
    completedOn: '',
    attemptNumber: null
  });
  const showAttemptDetails = !!state.attempt;

  const setAttempt = (attempt: any, completedOn: any, attemptNumber: any) => {
    setState({ attempt, completedOn, attemptNumber });
  };

  const goBack = () => {
    setState({ attempt: null, completedOn: '', attemptNumber: null });
  };
  const builtAttemptNumber = (attemptsLength: number, index: number) => {
    return attemptsLength - index;
  };
  const getTitle = () => {
    return showAttemptDetails ? (
      <Box>
        Attempt no. {state.attemptNumber}
        <Typography>
          <SubText style={{ fontSize: 14, fontWeight: 400 }}>
            Completed on {formatUiDate(state.completedOn, 'MM/dd/yyyy')}
          </SubText>
        </Typography>
      </Box>
    ) : (
      <>{module}</>
    );
  };

  const handleDownloadResponse = () => {
    console.log(moduleData);
    const documentTitle = `Grade ${studentData?.gradeLevel}, ${module}, Attempt ${state.attemptNumber} - ${studentData?.firstName} ${studentData?.lastName}.pdf`;

    handlePrint({
      documentTitle
    });
  };

  return (
    <div ref={printRef} className={classes.container}>
      <Box overflow="auto" marginBottom="10px">
        <Box className={classes.noPrint} padding={showAttemptDetails ? 2.5 : 4}>
          <Typography style={{ fontSize: 28, fontWeight: 450, display: 'flex' }}>
            {showAttemptDetails && (
              <IconButton>
                <ArrowBackIcon onClick={goBack} style={{ verticalAlign: 'middle' }} />
              </IconButton>
            )}
            {getTitle()}
          </Typography>
          {!showAttemptDetails && (
            <Typography>
              <SubText style={{ fontSize: 14, fontWeight: 400 }}>
                Completed on {formatUiDate(moduleData.dateCompleted, 'MM/dd/yyyy')}
              </SubText>
            </Typography>
          )}
        </Box>
        <Divider />
        <Box padding={'12px 32px'} className={printClasses.containerTitle}>
          <Box display="flex" flex={1}>
            <Typography className={printClasses.name} style={{ fontSize: 18, fontWeight: 500 }}>
              {studentData?.firstName} {studentData?.lastName}
            </Typography>
            <GradeLevelChip text={t(studentData?.gradeLevel!)} />
          </Box>
          <Typography>
            <SubText className={printClasses.subTitle} style={{ fontSize: 12, fontWeight: 400 }}>
              Class
            </SubText>
            <Typography
              className={printClasses.subTitle}
              style={{ fontSize: 14, fontWeight: 450, display: 'flex' }}
            >
              {studentData?.classrooms?.[0].name ?? ''}
            </Typography>
          </Typography>
          <Divider style={{ marginTop: 5 }} />
        </Box>

        {showAttemptDetails ? (
          <AttemptsDetails activities={state.attempt} />
        ) : (
          <Box padding="0 32px">
            <Box>
              {moduleData.attempts.map((x, idx) => (
                <Box className={classes.container}>
                  <Paper className={classes.paper} variant="outlined">
                    <Box display="flex" justifyContent="space-between">
                      <Box>
                        <Typography style={{ fontSize: 14, fontWeight: 450 }}>
                          Attempt no. {builtAttemptNumber(moduleData.attempts.length, idx)}
                        </Typography>
                        <Typography
                          style={{ display: 'flex', flexDirection: 'column', marginRight: 24 }}
                        >
                          <SubText style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
                            Completed on {formatUiDate(x.dateCompleted, 'MM/dd/yyyy')}
                          </SubText>
                        </Typography>
                      </Box>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() =>
                          setAttempt(
                            x.activities,
                            x.dateCompleted,
                            builtAttemptNumber(moduleData.attempts.length, idx)
                          )
                        }
                        style={{ fontSize: 12, fontWeight: 450, whiteSpace: 'nowrap' }}
                      >
                        <ArrowForward style={{ color: '#006DB8', fontSize: 12, fontWeight: 450 }} />
                        View Details
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Box className={classes.footer}>
        <Button style={{ marginRight: 24 }} variant="outlined" onClick={closeModal}>
          Close
        </Button>
        {showAttemptDetails && (
          <Button
            style={{ marginRight: 24 }}
            color="primary"
            variant="contained"
            onClick={handleDownloadResponse}
          >
            Download Attempt
          </Button>
        )}
      </Box>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between',
    flexDirection: 'column',
    margin: '10px 0px',
    overflow: 'hidden',
    '@media print': { overflow: 'none' }
  },
  itemContainer: {
    display: 'flex',
    flex: 1
  },
  body: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 68,
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    paddingTop: 15,
    '@media print': { display: 'none' }
  },
  paper: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: 76,
    padding: '8px 16px 8px 16px',
    backgroundColor: '#FCFCFC'
  },
  noPrint: {
    '@media print': { display: 'none' }
  }
}));

const usePrintStyles = makePrintStyles({
  name: { fontSize: '42px !important' },
  title: { fontSize: '32px !important' },
  subTitle: { fontSize: '24px !important', padding: '12px 24px !important' },
  containerTitle: { padding: '24px 32px !important' }
});

export default (props: any) => (
  <PrintProvider>
    <ViewAllAttemptsModal {...props} />
  </PrintProvider>
);
